import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { FadeIn } from '@/components/marketing/FadeIn';

interface Feature {
  title: string;
  body: React.ReactNode;
}

const FEATURES: Feature[] = [
  {
    title: 'Four platforms. One feed. Done.',
    body: (
      <>
        Reddit, Bluesky, YouTube, and X — unified in a single, chronological stream. No context
        switching. No losing your thread. SocialScoop fetches in the background every 20 minutes
        so that when you arrive, it's already curated and ready. You read. You don't hunt.
      </>
    ),
  },
  {
    title: 'Listening Rules — rules you write, intelligence that applies them',
    body: (
      <>
        Write what you want in plain English. "Show me posts about climate science from people
        I follow on Bluesky." "Prioritize threads from this subreddit." The Intensity Gauge (0–5)
        lets you tune rather than toggle — so your feed gets better every time you use it, not
        just louder.
      </>
    ),
  },
  {
    title: 'Digital Fencing™ — fences, not filters',
    body: (
      <>
        There's a difference between being moderated and being in control. Fences are yours —
        set them on any topic, account, or content type. SocialScoop de-emphasizes or removes
        what drains you, without deleting it. You decide what you see. We don't decide for you.
      </>
    ),
  },
  {
    title: 'Content Vault — nothing hidden from you, everything hidden for you',
    body: (
      <>
        Every fenced post goes to the Vault: PIN-protected, fully reviewable whenever you want.
        This isn't censorship. It's curation — and it's always yours to undo. SocialScoop doesn't
        make moderation decisions. It executes yours.
      </>
    ),
  },
  {
    title: 'AI scoring — advisory intelligence, not algorithmic control',
    body: (
      <>
        Claude scores every post for sentiment and toxicity — surfacing what your rules would
        want to act on. The scores inform your filters. They never override them. You remain the
        final call. Always. Powered by Claude AI, with OpenAI and Gemini support coming soon.
      </>
    ),
  },
  {
    title: "Family Mode — a feed you'd actually hand your kids",
    body: (
      <>
        Parent Lock. PIN-protected content fences. Rules your children can't override — and you
        can update anytime. SocialScoop lets you set the household standard instead of deferring
        to a platform that profits from your child's engagement. You decide what's appropriate.
        Not an algorithm.
      </>
    ),
  },
  {
    title: 'Curator feeds — your curation is the contribution',
    body: (
      <>
        Make your feed public. Build a following. Academics, researchers, and professionals use
        SocialScoop to curate the discourse in their field, add their own writing and papers via
        manual feed, and share expert-filtered streams with readers who trust their judgment.
        Your standards become someone else's signal. Founding Members can earn from their audience.
      </>
    ),
  },
  {
    title: 'Cross-platform posting',
    body: (
      <>
        Like, comment, and reply across Reddit, Bluesky, YouTube, and X without leaving your
        feed. The same deliberate control that governs what you consume extends to what you
        publish. One place. Every platform. No switching.
      </>
    ),
  },
  {
    title: 'Ambient dashboard — a reading room, not a notification center',
    body: (
      <>
        Choose a background theme and ambient music (Lo-fi / Nature / Piano / Silence) in
        Settings → Appearance. Customize your accent color with a full color wheel. SocialScoop
        is designed to feel like a space worth returning to — calm, focused, and entirely
        shaped by you.
      </>
    ),
  },
];

const Features: React.FC = () => {
  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Header />

      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <FadeIn>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Features</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1]">
            Everything your feed should be.<br className="hidden sm:block" /> Nothing it shouldn't.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Built around your standards — not a platform's engagement targets.
          </p>
        </FadeIn>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {FEATURES.map((f, i) => (
            <FadeIn key={f.title} delay={(i % 2) * 100}>
              <article className="rounded-xl border border-border bg-card p-7 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 h-full">
                <h2 className="text-lg font-semibold tracking-tight">{f.title}</h2>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </article>
            </FadeIn>
          ))}
        </div>
        <p className="mt-8 text-xs text-muted-foreground text-center">
          AI scoring powered by Claude · OpenAI and Google Gemini support coming soon
        </p>
      </section>

      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-semibold tracking-tight">Ready to own your feed?</h2>
          <p className="mt-4 text-base text-muted-foreground">
            Start for $1. Scales with what you use. No lock-in.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Button asChild>
              <Link to="/auth">Start for free</Link>
            </Button>
            <Link
              to="/how-it-works"
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              See how it works →
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 SocialScoop.ai — Built for people, not platforms.</div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
            <Link to="/how-it-works" className="hover:text-foreground transition-colors">How It Works</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/data-deletion" className="hover:text-foreground transition-colors">Data Deletion</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Features;
